import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { AppPreferences } from '../../../core/services/app-preferences';
import { LeadTrackingState } from '../../../lead-tracking/lead-tracking-state';
import { AppIcon } from '../../../shared/app-icon/app-icon';
import { FundCalculatorApi } from '../../../personal-fund-request/services/fund-calculator-api';

@Component({
  selector: 'app-hero-section',
  imports: [FormsModule, RouterLink, AppIcon],
  templateUrl: './hero-section.html',
  styleUrl: './hero-section.scss',
})
export class HeroSection {
  readonly preferences = inject(AppPreferences);
  private readonly fundCalculatorApi = inject(FundCalculatorApi);
  private readonly trackingState = inject(LeadTrackingState);
  private readonly router = inject(Router);

  nationalId = '';
  mobileNumber = '';
  nationalIdTouched = false;
  mobileNumberTouched = false;
  trackingError = '';
  isSearching = false;

  get isNationalIdValid(): boolean {
    return /^[12]\d{9}$/.test(this.nationalId);
  }

  get isMobileNumberValid(): boolean {
    return /^5\d{8}$/.test(this.mobileNumber);
  }

  get canSearch(): boolean {
    return this.isNationalIdValid && this.isMobileNumberValid && !this.isSearching;
  }

  updateNationalId(value: string): void {
    this.nationalId = value.replace(/\D/g, '').slice(0, 10);
    this.trackingError = '';
  }

  updateMobileNumber(value: string): void {
    this.mobileNumber = value.replace(/\D/g, '').slice(0, 9);
    this.trackingError = '';
  }

  async searchRequest(): Promise<void> {
    this.nationalIdTouched = true;
    this.mobileNumberTouched = true;
    this.trackingError = '';

    if (!this.canSearch) {
      return;
    }

    this.isSearching = true;

    try {
      const result = await firstValueFrom(
        this.fundCalculatorApi.initLeadVerification(this.getLeadVerificationPayload()),
      );

      await this.navigateToTrackingOtp((result.resendOtpEveryMinutes ?? 0) * 60 || 30);
    } catch (error) {
      if (this.isCustomerNotFoundError(error)) {
        await this.navigateToTrackingOtp(30);
        return;
      }

      this.trackingError = this.getErrorMessage(error, 'landing.tracking.submitError');
    } finally {
      this.isSearching = false;
    }
  }

  private async navigateToTrackingOtp(otpCountdownSeconds: number): Promise<void> {
    this.trackingState.setPendingVerification({
      ...this.getLeadVerificationPayload(),
      otpCountdownSeconds,
    });
    await this.router.navigateByUrl('/tracking-otp');
  }

  private getLeadVerificationPayload(): { nationalID: string; mobileNumber: string } {
    return {
      nationalID: this.nationalId,
      mobileNumber: this.mobileNumber,
    };
  }

  private getErrorMessage(error: unknown, fallbackKey: string): string {
    const fallbackMessage = this.preferences.t(fallbackKey);

    if (error instanceof HttpErrorResponse) {
      return this.extractApiMessage(error.error) || fallbackMessage;
    }

    if (error instanceof Error) {
      return this.isTechnicalErrorMessage(error.message)
        ? fallbackMessage
        : error.message || fallbackMessage;
    }

    return fallbackMessage;
  }

  private isTechnicalErrorMessage(message: string): boolean {
    return (
      message.startsWith('Http failure response') ||
      message === 'Failed to fetch' ||
      message === 'Could not verify OTP.' ||
      message === 'Could not send OTP.'
    );
  }

  private isCustomerNotFoundError(error: unknown): boolean {
    const message =
      error instanceof HttpErrorResponse
        ? this.extractApiMessage(error.error)
        : error instanceof Error
          ? error.message
          : '';
    const normalizedMessage = message.trim().toLowerCase();

    return (
      normalizedMessage.includes('لم يتم العثور على العميل') ||
      normalizedMessage.includes('customer not found') ||
      normalizedMessage.includes('client not found') ||
      normalizedMessage.includes('lead not found')
    );
  }

  private extractApiMessage(errorBody: unknown): string {
    if (typeof errorBody === 'string') {
      return errorBody.trim();
    }

    if (!errorBody || typeof errorBody !== 'object') {
      return '';
    }

    const body = errorBody as Record<string, unknown>;
    const message = body['message'];

    return typeof message === 'string' && message.trim() ? message.trim() : '';
  }
}
